import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  PieChart, 
  Calendar, 
  ArrowUp, 
  ArrowDown, 
  DollarSign, 
  ShoppingCart, 
  TrendingUp
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './PaymentBreakdownModal'
import { API_URL } from '../../api/config';

const CHART_COLORS = {
  pie: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316', '#A855F7'],
  bar: ['#4F46E5', '#7C3AED', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6', '#14B8A6', '#F97316']
};

const ExpenseReport = () => {
  const { userToken: token } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [timeFilter, setTimeFilter] = useState('today');
  const [stats, setStats] = useState({});
  const [itemStats, setItemStats] = useState([]);
  const [chartType, setChartType] = useState('pie');
  const [sortConfig, setSortConfig] = useState({ key: 'count', direction: 'desc' });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [token]);

  useEffect(() => {
    if (subscriptionStatus === 'active') {
      fetchData();
    }
  }, [timeFilter, token, subscriptionStatus]);

  const checkSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/subscription/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subscription status');
      }
      
      const data = await response.json();
      setSubscriptionStatus(data.status);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    navigate('/subscription');
  };

  const fetchData = async () => {
    try {
      const endDate = new Date();
      let startDate = new Date();

      switch (timeFilter) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          break;
        case '7days':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '1month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case '3months':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case '6months':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          break;
      }

      const response = await fetch(
        `${API_URL}/expenses?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      setExpenses(data);
      
      const statsResponse = await fetch(`${API_URL}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsResponse.json();
      setStats(statsData);

      // Calculate detailed item statistics
      const itemStats = {};
      data.forEach(expense => {
        if (!itemStats[expense.item]) {
          itemStats[expense.item] = {
            item: expense.item,
            count: 0,
            totalSpent: 0,
            avgPrice: 0,
            firstPurchase: new Date(expense.date),
            lastPurchase: new Date(expense.date),
            frequency: 0
          };
        }
        
        const stat = itemStats[expense.item];
        stat.count += expense.quantity;
        stat.totalSpent += expense.totalPrice;
        stat.avgPrice = stat.totalSpent / stat.count;
        stat.firstPurchase = new Date(Math.min(stat.firstPurchase, new Date(expense.date)));
        stat.lastPurchase = new Date(Math.max(stat.lastPurchase, new Date(expense.date)));
      });

      Object.values(itemStats).forEach(stat => {
        const monthsDiff = (stat.lastPurchase - stat.firstPurchase) / (1000 * 60 * 60 * 24 * 30.44);
        stat.frequency = monthsDiff > 0 ? (stat.count / monthsDiff).toFixed(2) : stat.count;
      });

      setItemStats(Object.values(itemStats));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getExpensesByCategory = () => {
    const categories = {};
    expenses.forEach(expense => {
      categories[expense.item] = (categories[expense.item] || 0) + expense.totalPrice;
    });
    return Object.entries(categories).map(([name, value]) => ({
      name,
      value,
      label: `${name}: ₹${value.toFixed(0)}`
    }));
  };

  const sortData = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedItems = () => {
    const sortedItems = [...itemStats];
    const { key, direction } = sortConfig;
    
    return sortedItems.sort((a, b) => {
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const PieChartComponent = () => {
    const data = getExpensesByCategory();
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    // SVG dimensions
    const width = 300;
    const height = 300;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(centerX, centerY) - 40;
    
    let startAngle = 0;
    
    const slices = data.map((item, index) => {
      const percentage = item.value / total;
      const endAngle = startAngle + percentage * 2 * Math.PI;
      
      // Calculate the coordinates for the arc
      const x1 = centerX + radius * Math.cos(startAngle);
      const y1 = centerY + radius * Math.sin(startAngle);
      const x2 = centerX + radius * Math.cos(endAngle);
      const y2 = centerY + radius * Math.sin(endAngle);
      
      // Determine if the arc should be drawn as a large arc
      const largeArcFlag = percentage > 0.5 ? 1 : 0;
      
      // Generate the SVG path for the slice
      const path = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
      
      // Calculate the position for the label
      const labelAngle = startAngle + (percentage * Math.PI);
      const labelRadius = radius * 0.7;
      const labelX = centerX + labelRadius * Math.cos(labelAngle);
      const labelY = centerY + labelRadius * Math.sin(labelAngle);
      
      // Update the start angle for the next slice
      const currentStartAngle = startAngle;
      startAngle = endAngle;
      
      return {
        path,
        color: CHART_COLORS.pie[index % CHART_COLORS.pie.length],
        labelX,
        labelY,
        percentage,
        item
      };
    });

    return (
      <div className="flex flex-col items-center">
        <svg width={width} height={height}>
          {slices.map((slice, index) => (
            <g key={index}>
              <path
                d={slice.path}
                fill={slice.color}
              />
              <text
                x={slice.labelX}
                y={slice.labelY}
                fill="white"
                fontSize="12"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {Math.round(slice.percentage * 100)}%
              </text>
            </g>
          ))}
        </svg>
        <div className="flex flex-wrap justify-center gap-3 mt-4">
          {data.map((item, index) => (
            <div key={index} className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-1" 
                style={{ backgroundColor: CHART_COLORS.pie[index % CHART_COLORS.pie.length] }}
              ></div>
              <span className="text-sm text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const BarChartComponent = () => {
    const data = getSortedItems();
    const maxValue = Math.max(...data.map(item => item.totalSpent));
    
    // SVG dimensions
    const width = 500;
    const height = 300;
    const padding = 40;
    const chartWidth = width - padding * 2;
    const chartHeight = height - padding * 2;
    const barPadding = 5;
    const barWidth = (chartWidth / Math.min(data.length, 10)) - barPadding;
    
    const bars = data.slice(0, 10).map((item, index) => {
      const barHeight = (item.totalSpent / maxValue) * chartHeight;
      const x = padding + index * (barWidth + barPadding);
      const y = height - padding - barHeight;
      
      return {
        x,
        y,
        width: barWidth,
        height: barHeight,
        color: CHART_COLORS.bar[index % CHART_COLORS.bar.length],
        item
      };
    });

    return (
      <div className="flex justify-center">
        <svg width={width} height={height}>
          {bars.map((bar, index) => (
            <g key={index}>
              <rect
                x={bar.x}
                y={bar.y}
                width={bar.width}
                height={bar.height}
                fill={bar.color}
                rx={2}
              />
              <text
                x={bar.x + bar.width / 2}
                y={height - padding / 2}
                fill="#4B5563"
                fontSize="10"
                textAnchor="middle"
              >
                {bar.item.item.substring(0, 8)}
              </text>
              <text
                x={bar.x + bar.width / 2}
                y={bar.y - 10}
                fill="#4B5563"
                fontSize="10"
                textAnchor="middle"
              >
                ₹{Math.round(bar.item.totalSpent)}
              </text>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  // Show loading indicator while checking subscription
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading expense report...</p>
        </div>
      </div>
    );
  }
  
  // Show error message if there's an error
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 mb-4 text-xl">Error: {error}</div>
          <button 
            onClick={checkSubscriptionStatus}
            className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition duration-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  // Show subscription prompt if user is not subscribed
  if (subscriptionStatus !== 'active') {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Premium Feature</h2>
          <div className="bg-indigo-50 p-4 rounded-lg mb-6">
            <span className="text-indigo-600 text-4xl">💰</span>
            <p className="text-gray-700 mt-3">
              The Expense Report is a premium feature that requires an active subscription.
            </p>
          </div>
          <button 
            onClick={handleSubscribe}
            className="w-full px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg shadow hover:bg-indigo-700 transition duration-200"
          >
            Subscribe Now
          </button>
        </div>
      </div>
    );
  }

  // Render expense report for subscribed users
  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      <div className="p-4 md:p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Expense Report</h1>
          <div className="flex bg-white rounded-lg shadow-sm p-1">
            <button
              className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                chartType === 'pie' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setChartType('pie')}
            >
              <PieChart size={16} className="mr-1" />
              <span>Pie</span>
            </button>
            <button
              className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                chartType === 'bar' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setChartType('bar')}
            >
              <BarChart3 size={16} className="mr-1" />
              <span>Bar</span>
            </button>
          </div>
        </div>

        <div className="flex overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {['today', '7days', '1month', '3months', '6months', '1year'].map(filter => (
            <button
              key={filter}
              className={`flex items-center px-4 py-2 mr-2 rounded-lg transition-colors whitespace-nowrap ${
                timeFilter === filter 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
              onClick={() => setTimeFilter(filter)}
            >
              <Calendar size={16} className="mr-1" />
              <span>{filter}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-indigo-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-indigo-100 text-sm mb-1">Total Expenses</p>
                <p className="text-xl font-bold">₹{stats.totalExpenses?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || 0}</p>
              </div>
              <div className="bg-indigo-500 p-2 rounded-md">
                <DollarSign size={20} />
              </div>
            </div>
          </div>
          
          <div className="bg-green-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-green-100 text-sm mb-1">Average Expense</p>
                <p className="text-xl font-bold">₹{stats.avgExpense?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || 0}</p>
              </div>
              <div className="bg-green-500 p-2 rounded-md">
                <TrendingUp size={20} />
              </div>
            </div>
          </div>
          
          <div className="bg-amber-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-amber-100 text-sm mb-1">Highest Expense</p>
                <p className="text-xl font-bold">₹{stats.maxExpense?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || 0}</p>
              </div>
              <div className="bg-amber-500 p-2 rounded-md">
                <ArrowUp size={20} />
              </div>
            </div>
          </div>
          
          <div className="bg-violet-600 text-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-violet-100 text-sm mb-1">Total Transactions</p>
                <p className="text-xl font-bold">{stats.count?.toLocaleString('en-IN') || 0}</p>
              </div>
              <div className="bg-violet-500 p-2 rounded-md">
                <ShoppingCart size={20} />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          {chartType === 'pie' ? <PieChartComponent /> : <BarChartComponent />}
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Detailed Item Statistics</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortData('item')}
                  >
                    <div className="flex items-center">
                      Item
                      {sortConfig.key === 'item' && (
                        sortConfig.direction === 'asc' 
                          ? <ArrowUp size={14} className="ml-1" />
                          : <ArrowDown size={14} className="ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortData('count')}
                  >
                    <div className="flex items-center">
                      Quantity
                      {sortConfig.key === 'count' && (
                        sortConfig.direction === 'asc' 
                          ? <ArrowUp size={14} className="ml-1" />
                          : <ArrowDown size={14} className="ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortData('totalSpent')}
                  >
                    <div className="flex items-center">
                      Total Spent
                      {sortConfig.key === 'totalSpent' && (
                        sortConfig.direction === 'asc' 
                          ? <ArrowUp size={14} className="ml-1" />
                          : <ArrowDown size={14} className="ml-1" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortData('avgPrice')}
                  >
                    <div className="flex items-center">
                      Avg Price
                      {sortConfig.key === 'avgPrice' && (
                        sortConfig.direction === 'asc' 
                          ? <ArrowUp size={14} className="ml-1" />
                          : <ArrowDown size={14} className="ml-1" />
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSortedItems().map((item, index) => (
                  <tr key={item.item} className={index % 2 === 0 ? '' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.item}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.count.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">₹{item.totalSpent.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">₹{item.avgPrice.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;